import React, { useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const INK = "#1A1A2E";
const MUTED = "#5b5e69";
const GREEN = "#0F7A3E";
const RED = "#B4241E";

/**
 * BR-19 · Self-Serve Onboarding for Starter tier.
 *
 * 3 steps in order:
 *   1. Contractor enters phone.
 *   2. We detect carrier + show exact *72 / **21* code to dial.
 *   3. They tap TEST CALL — ARIA (Retell) dials them back.
 *      When they answer AND hear ARIA, they tap "YES, IT'S FORWARDING".
 *      That call marks `forwarding_verified_at` → guarantee window opens.
 */
export default function SelfServeOnboardPage() {
  const [search] = useSearchParams();
  const contractorId = search.get("contractor_id") || "";
  const [phone, setPhone] = useState("");
  const [sheet, setSheet] = useState(null);
  const [testCall, setTestCall] = useState(null);
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const fetchSheet = async () => {
    setErr(""); setBusy("carrier");
    try {
      const r = await axios.get(`${API}/self-serve/carrier`, { params: { phone } });
      if (!r.data?.ok) { setErr(r.data?.error === "invalid_phone" ? "Enter a valid 10-digit US number." : "Couldn't detect your carrier."); }
      else setSheet(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(""); }
  };

  const fireTestCall = async () => {
    setErr(""); setBusy("test");
    try {
      const r = await axios.post(`${API}/self-serve/test-call`, {
        contractor_id: contractorId || "self_serve_visitor", phone,
      });
      setTestCall(r.data);
      if (!r.data?.ok) setErr("Couldn't place the test call. Try again in a minute.");
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(""); }
  };

  const confirmForwarded = async () => {
    setErr(""); setBusy("verify");
    try {
      await axios.post(`${API}/self-serve/mark-verified`, {
        contractor_id: contractorId || "self_serve_visitor",
      });
      setVerified(true);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="self-serve-page" style={{ minHeight: "100vh", background: "#F5F6FA", padding: "28px 16px 60px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", background: "#fff", padding: 26, borderRadius: 14, boxShadow: "0 8px 24px rgba(10,22,40,0.08)" }}>
        <p style={{ margin: 0, color: GOLD, fontSize: 13, letterSpacing: 3, fontFamily: "'Bebas Neue', sans-serif" }}>
          NEARNOW · 5-MINUTE SELF-SETUP
        </p>
        <h1 className="font-display" style={{ color: NAVY, fontSize: 40, margin: "6px 0 6px", lineHeight: 1 }}>
          POINT YOUR CALLS AT ARIA.
        </h1>
        <p style={{ color: MUTED, marginTop: 0, fontSize: 16 }}>
          Three taps. No app. No new number. Your existing line keeps ringing — but ARIA answers whenever you can't.
        </p>

        {/* Step 1 — Phone */}
        <StepBlock step={1} title="Which phone rings when a customer calls?" done={!!sheet}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <label className="nn-label">Business phone (10 digits, US)</label>
              <input
                data-testid="self-serve-phone-input"
                className="nn-input"
                type="tel"
                inputMode="numeric"
                placeholder="401 555 0123"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <button
              data-testid="self-serve-detect-btn"
              className="btn-navy tap"
              disabled={busy === "carrier" || !phone}
              onClick={fetchSheet}
              style={{ minWidth: 160 }}
            >
              {busy === "carrier" ? "…" : "DETECT CARRIER"}
            </button>
          </div>
        </StepBlock>

        {/* Step 2 — Forwarding code */}
        {sheet && (
          <StepBlock step={2} title={`Dial this on ${sheet.carrier.name}`} done={!!testCall && testCall.ok}>
            <div
              data-testid="self-serve-forwarding-code"
              style={{
                background: NAVY, color: GOLD, padding: "18px 20px",
                borderRadius: 10, textAlign: "center",
                fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 3, fontSize: 34,
              }}
            >
              {sheet.enable_code}
            </div>
            {sheet.aria_number_missing && (
              <div data-testid="aria-number-missing" style={warnBox}>
                Heads up: we haven't provisioned your ARIA number yet, so the code above only has the carrier prefix.
                Text us at contact@nearnow.biz — takes 5 minutes to fix from our side.
              </div>
            )}
            <ol style={{ paddingLeft: 20, marginTop: 12, color: INK, lineHeight: 1.6 }}>
              {sheet.steps.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s}</li>)}
            </ol>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
              To turn forwarding off later: dial <code>{sheet.disable_code}</code>.
            </div>
            <button
              data-testid="self-serve-testcall-btn"
              className="btn-gold tap"
              disabled={busy === "test"}
              onClick={fireTestCall}
              style={{ width: "100%", marginTop: 16 }}
            >
              {busy === "test" ? "…" : "TEST CALL ME NOW"}
            </button>
            {testCall && (
              <div data-testid="self-serve-testcall-result" style={testCall.ok ? okBox : warnBox}>
                {testCall.detail}
              </div>
            )}
          </StepBlock>
        )}

        {/* Step 3 — Confirm */}
        {testCall && testCall.ok && (
          <StepBlock step={3} title="Did ARIA reach you?" done={verified}>
            <p style={{ color: INK, margin: "0 0 12px" }}>
              If you heard ARIA introduce herself, tap the button. That starts your free-month guarantee window.
            </p>
            <button
              data-testid="self-serve-confirm-btn"
              className="btn-gold tap"
              disabled={busy === "verify" || verified}
              onClick={confirmForwarded}
              style={{ width: "100%" }}
            >
              {verified ? "✓ VERIFIED — YOU'RE LIVE" : (busy === "verify" ? "…" : "YES, ARIA ANSWERED")}
            </button>
            {verified && (
              <div data-testid="self-serve-success" style={okBox}>
                You're activated. ARIA is now answering every missed call. First 30 days on us — if she doesn't
                answer at least 10 calls you would have missed, month two is free.
              </div>
            )}
          </StepBlock>
        )}

        {err && <div data-testid="self-serve-err" style={errBox}>{err}</div>}

        <p style={{ color: MUTED, fontSize: 12, marginTop: 22, textAlign: "center" }}>
          Prefer a human walkthrough? Reply to your welcome text with <strong>CALL ME</strong> and we'll ring back in 20 minutes.
        </p>
      </div>
    </div>
  );
}

const StepBlock = ({ step, title, done, children }) => (
  <div
    style={{
      marginTop: 22, padding: 18, borderRadius: 12,
      border: `1.5px solid ${done ? GREEN : "rgba(10,22,40,0.12)"}`,
      background: done ? "#F0FBF4" : "#fff",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <span
        style={{
          width: 26, height: 26, borderRadius: 999, background: done ? GREEN : NAVY,
          color: done ? "#fff" : GOLD, display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800, fontSize: 13,
        }}
      >
        {done ? "✓" : step}
      </span>
      <div style={{ fontWeight: 800, color: NAVY, fontSize: 17 }}>{title}</div>
    </div>
    {children}
  </div>
);

const okBox = { marginTop: 10, padding: "10px 14px", background: "#E7F4EC", color: GREEN, border: `1px solid ${GREEN}`, borderRadius: 8, fontSize: 14 };
const errBox = { marginTop: 12, padding: "10px 14px", background: "#FDECEA", color: RED, border: `1px solid ${RED}`, borderRadius: 8, fontSize: 14 };
const warnBox = { marginTop: 10, padding: "10px 14px", background: "#FFF4DC", color: "#8A6100", border: "1px solid #D4A017", borderRadius: 8, fontSize: 14 };
